import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { message } from 'antd';
import { getSectionIdByRoute, isPlaybookRoute } from '../config/routeMapping';
import { tr } from '../config/strings';
import { usePlaybookData } from '../hooks/usePlaybookData';
import { usePlaybookLang } from '../hooks/usePlaybookLang';
import PlaybookFab from './PlaybookFab';
import PlaybookPanel from './PlaybookPanel';

const WIDE_STORAGE_KEY = 'host_playbook_wide';
const HIGHLIGHT_MS = 2000;

const useViewport = () => {
  const [width, setWidth] = useState(1440);
  useEffect(() => {
    const update = () => setWidth(window.innerWidth);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return { isMobile: width < 768, isNarrow: width < 820 };
};

const readWide = (): boolean => {
  try {
    return window.localStorage.getItem(WIDE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * In-product user guide (TAP-1268). Floating button + side panel that reads the guide from markdown files.
 * Mounted once in `_app.tsx`; the panel keeps its state while the user moves between screens.
 */
const PlaybookWidget = () => {
  const router = useRouter();
  const { isMobile, isNarrow } = useViewport();
  const [lang, changeLang] = usePlaybookLang();

  const [open, setOpen] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const [wide, setWide] = useState(false);
  const [viewingId, setViewingId] = useState('');
  const [highlightId, setHighlightId] = useState<string | null>(null);
  // Route the panel was last aligned with, so reopening on another screen jumps to that screen's section.
  const alignedRouteRef = useRef<string | null>(null);

  const { data, isLoading, error } = usePlaybookData(hasOpened);

  useEffect(() => {
    setWide(readWide());
  }, []);

  const toggleWide = useCallback(() => {
    setWide((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(WIDE_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // storage unavailable: keep the choice for this session only
      }
      return next;
    });
  }, []);

  const close = useCallback(() => setOpen(false), []);

  const highlight = useCallback((id: string) => {
    setHighlightId(id);
    window.setTimeout(() => setHighlightId((current) => (current === id ? null : current)), HIGHLIGHT_MS);
  }, []);

  const openAt = useCallback(
    (sectionId?: string) => {
      setOpen(true);
      setHasOpened(true);
      if (sectionId) {
        setViewingId(sectionId);
        highlight(sectionId);
        return;
      }
      if (alignedRouteRef.current !== router.pathname || !viewingId) {
        alignedRouteRef.current = router.pathname;
        setViewingId(getSectionIdByRoute(router.pathname) ?? '');
      }
    },
    [router.pathname, viewingId, highlight],
  );

  const changeViewing = useCallback(
    (id: string) => {
      setViewingId(id);
      highlight(id);
    },
    [highlight],
  );

  const copyLink = useCallback(
    async (id: string) => {
      const url = `${window.location.origin}${router.asPath.split(/[?#]/)[0]}?pb=${encodeURIComponent(id)}`;
      try {
        await navigator.clipboard.writeText(url);
        message.success(tr('linkCopied', lang));
      } catch {
        message.error(tr('linkCopyFailed', lang));
      }
    },
    [router.asPath, lang],
  );

  // Shared link: ?pb=<section code> opens the guide at that section.
  useEffect(() => {
    if (!router.isReady) return;
    const pb = router.query.pb;
    if (typeof pb !== 'string' || !pb) return;
    alignedRouteRef.current = router.pathname;
    openAt(pb);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query.pb]);

  // Panel stays open while the user changes screen: follow the screen being used.
  useEffect(() => {
    if (!open || alignedRouteRef.current === router.pathname) return;
    alignedRouteRef.current = router.pathname;
    const sectionId = getSectionIdByRoute(router.pathname);
    if (sectionId) setViewingId(sectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!isPlaybookRoute(router.pathname)) return null;

  return (
    <>
      {!open && <PlaybookFab lang={lang} isMobile={isMobile} onClick={() => openAt()} />}
      {open && (
        <PlaybookPanel
          lang={lang}
          onLangChange={changeLang}
          wide={wide}
          onToggleWide={toggleWide}
          data={data}
          isLoading={isLoading}
          error={error ?? null}
          viewingId={viewingId}
          onViewingChange={changeViewing}
          highlightId={highlightId}
          onCopyLink={copyLink}
          onClose={close}
          isMobile={isMobile}
          isNarrow={isNarrow}
        />
      )}
    </>
  );
};

export default PlaybookWidget;
